import * as React from 'react';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';
import FormErrorMessage from '../../../components/ErrorMessage';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';
import { IncidentFormTypes } from '../IncidentSubmit';

interface AddWitnessModalTypes {
	closeModal?: closeModalType;
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
}

const AddWitnessModal: React.FC<AddWitnessModalTypes> = ({ closeModal, incidentFormData, setIncidentFormData }) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const initialValues = {
		name: '',
		role: '',
		phone: '',
		email: ''
	};

	const phoneRegExp =
		/^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

	const validationSchema = Yup.object({
		name: Yup.string().required('Required'),
		role: Yup.string().required('Required'),
		phone: Yup.string().matches(phoneRegExp, 'Phone number is not valid'),
		email: Yup.string().email('Please enter a valid email address')
	});

	const submitHandler = (values: any) => {
		const formData = {
			name: values.name as string,
			role: values.role as string,
			phone: values.phone as string,
			email: values.email as string
		};

		const currentWitnesses = incidentFormData.witnesses || [];

		const newWitnesses = [...currentWitnesses, formData];

		setIncidentFormData((prevState) => ({
			...prevState,

			witnesses: newWitnesses
		}));

		if (closeModal) {
			closeModal();
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={submitHandler}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form mtop-0">
						<>
							{showError ? (
								<FormErrorMessage message={formErrorMessage} />
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<div className="form-group">
													<label htmlFor="name" className="label">
														Full Name
													</label>

													<div className="form-field">
														<Field name="name" id="name" type="text" className="form-input" />

														<ErrorMessage name="name" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="role" className="label">
														Role
													</label>

													<div className="form-field">
														<Field
															name="role"
															id="role"
															type="text"
															className="form-input"
															placeholder={'E.g. teacher, parent, supervisor...'}
														/>

														<ErrorMessage name="role" component="div" className="error-message" />
													</div>
												</div>

												<div className="row">
													<div className="form-group col-md-6">
														<label htmlFor="phone" className="label">
															Phone
														</label>

														<div className="form-field">
															<Field name="phone" id="phone" type="text" className="form-input" />

															<ErrorMessage name="phone" component="div" className="error-message" />
														</div>
													</div>

													<div className="form-group col-md-6">
														<label htmlFor="email" className="label">
															Email Address
														</label>

														<div className="form-field">
															<Field name="email" id="email" type="text" className="form-input" />

															<ErrorMessage name="email" component="div" className="error-message" />
														</div>
													</div>
												</div>

												<Button className="btn-primary full-width" type="submit">
													Add to the list
												</Button>
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</>
					</Form>
				);
			}}
		</Formik>
	);
};

export default AddWitnessModal;
